import React, {useEffect, useImperativeHandle, useRef} from 'react';

import type {SnabbdomElementProps} from '../types';

/**
 * React renderer that supports basic Snabbdom functions for standard DOM elements:
 *
 * - `attrs`: attributes to set on the element
 * - `props`: DOM properties to assign to the element
 * - `on`: event listeners to attach to the element
 * - `hooks`: lifecycle hooks for the element
 */
export const SnabbdomRenderer = React.forwardRef<HTMLElement, SnabbdomElementProps>((props, forwardedRef) => {
  const ref = useRef<HTMLElement | null>(null);

  useImperativeHandle(forwardedRef, () => ref.current!);

  const Tag = props.tagName as any;

  const eventHandlers = useRef<Record<string, EventListener>>({});

  const updateEventHandlers = () => {
    if (!ref.current) {
      return;
    }

    // clear handlers
    if (eventHandlers.current) {
      Object.entries(eventHandlers.current).forEach(([event, handler]) => {
        ref.current?.removeEventListener(event, handler);
      });

      eventHandlers.current = {};
    }

    // add new handlers
    if (props.on) {
      Object.entries(props.on).forEach(([event, handler]) => {
        ref.current?.addEventListener(event, handler as any);
      });

      eventHandlers.current = props.on;
    }
  };

  useEffect(updateEventHandlers, [props.on]);

  // Snabbdom's `props` are assigned to the element as DOM properties, not set as attributes, and
  // React has no declarative equivalent for arbitrary ones. `innerHTML` is the exception: it is
  // rendered through `dangerouslySetInnerHTML` below, so assigning it here too would fight React's
  // ownership of the element's content.
  useEffect(() => {
    if (!props.props || !ref.current) {
      return;
    }

    for (const [name, value] of Object.entries(props.props)) {
      if (name === `innerHTML`) {
        continue;
      }
      (ref.current as any)[name] = value;
    }
  }, [props.props]);

  // note: not using any dependencies on purpose. panel lifecycle hooks should be assumed to be stable references. (they're usually class methods)
  // todo: use useEffectEvent for these instead
  useEffect(() => {
    // this technically runs before first render, but its fine as long as it's called before insert
    // it will always be truthy since this runs after first render
    const elm = ref.current!;

    props.hooks?.init?.();
    props.hooks?.insert?.({elm});

    return () => {
      // not exactly to spec, but our usages should make this safe
      props.hooks?.destroy?.({elm});
      props.hooks?.remove?.({elm});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ref.current) {
      props.hooks?.update?.({elm: ref.current});
      props.hooks?.postpatch?.(undefined, {elm: ref.current});
    }
  });

  if (props.innerHTML !== undefined) {
    const dangerouslySetInnerHTML = {
      // React requires __html as the dangerouslySetInnerHTML payload key.
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __html: props.innerHTML,
    };
    return <Tag ref={ref} {...props.attrs} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />;
  }

  return (
    <Tag ref={ref} {...props.attrs}>
      {props.children}
    </Tag>
  );
});
